import { InboxOutlined } from '@ant-design/icons'
import { Spin } from 'antd'
import { t } from 'i18next'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import {
  InitialMessageContainer,
  ListPageLayout,
  PageContentDropdown,
  SearchTable,
  Spacer,
  SubHeader,
} from '../../components'
import { SearchTableRow } from '../../components/SearchTable'
import { NO_DATA_PLACEHOLDER, searchMethods } from '../../constants/constants'
import {
  useDataSources,
  useDownloadFile,
  useSearchDocuments,
} from '../../hooks'
import i18n from '../../i18n/i18n'
import { RootState } from '../../store'
import { SearchQueryParams } from '../../store/reducers/searchResultInfoReducer'
import { BackEndSortOrder } from '../../types/generalTypes'
import { routePaths } from '../RootPage'
import { CreateSearchReviewModal } from './modals'

const FullPageContainer = styled.div`
  display: flex;
  margin-top: 100px;
  width: 100%;
  flex: 1 0;
`

const searchMethod = searchMethods.KEYWORD_SEARCH_METHOD // default search method
const MAX_RESULTS = 10000 // NLM only handles 10k results

const SearchPage = () => {
  const navigate = useNavigate()
  const { performSearch, searchLoading } = useSearchDocuments()
  const [firstSearchDone, setFirstSearchDone] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)

  const { searchResult, searchResultInfo } = useSelector((state: RootState) => {
    const { searchResult } = state.searchResultInfo
    const searchResultInfo = searchResult?.data || []
    return { searchResult, searchResultInfo }
  })

  const { query, sourceId, period, startDate, endDate }: SearchQueryParams =
    useSelector((state: RootState) => state.searchResultInfo.searchQueryParams)

  const { dataSourceList } = useDataSources({})
  const dataSource = useMemo(
    () => dataSourceList.find((source) => source.id === sourceId),
    [dataSourceList, sourceId]
  )

  const downloadCsv = useDownloadFile()

  const formattedData: SearchTableRow[] = useMemo(
    () =>
      (searchResultInfo || []).map((document, index: number) => {
        const language = i18n.language
        const title =
          typeof document?.title === 'string'
            ? document.title
            : document?.title?.[language] ?? NO_DATA_PLACEHOLDER
        const documentLink =
          typeof document?.documentLink === 'string'
            ? document.documentLink
            : document?.documentLink?.[language] ?? ''

        return {
          ...document,
          key: index.toString(),
          id: document._id ?? `missing-id-${index}`,
          title,
          publicationName: document?.publicationName ?? NO_DATA_PLACEHOLDER,
          authors: document?.authors ?? [],
          publicationDate:
            document?.publicationDate ??
            document?.createdDate ??
            NO_DATA_PLACEHOLDER,
          documentLink,
          language,
          csv: document?.csv ?? '',
        }
      }),
    [searchResultInfo]
  )

  useEffect(() => {
    if (searchResultInfo.length > 0) {
      setFirstSearchDone(true)
    }
  }, [searchResultInfo])

  useEffect(() => {
    setCurrentPage(searchResult.page || 0)
  }, [searchResult.page])

  const handleOnRowClick = useCallback(
    (recordId: string) => {
      navigate(`${routePaths.SEARCH_DOCUMENT_DETAILS}/${recordId}`)
    },
    [navigate]
  )

  const handleSearchQueryExport = useCallback(() => {
    if (searchResult.csv) {
      downloadCsv(searchResult.csv, 'search_results.csv')
    }
  }, [searchResult.csv, downloadCsv])

  const refreshData = useCallback(
    (page: number, searchField?: string, searchOrder?: BackEndSortOrder) => {
      setCurrentPage(page)
      performSearch({
        searchText: query,
        sourceId: sourceId || '',
        period: period || '',
        startDate: startDate || '',
        endDate: endDate || '',
        sortField: searchField,
        sortOrder: searchOrder,
        page,
      })
    },
    [performSearch, query, sourceId, period, startDate, endDate, setCurrentPage]
  )

  const totalResult = useMemo(() => {
    if (!searchResult) return { currentPage: 0, total: 0 }

    const totalResults = searchResult.totalResults
      ? parseInt(searchResult.totalResults)
      : 0
    if (isNaN(totalResults)) return { currentPage: 0, total: 0 }
    //  cap total results to 10k cause of VESPA
    const cappedTotal = Math.min(totalResults, MAX_RESULTS)
    const totalPages = Math.ceil(cappedTotal / (searchResult.pageSize || 100))
    return {
      currentPage: searchResultInfo?.length || 0,
      total: totalPages * (searchResult.pageSize || 100), // cap total to 100 pages cause of VESPA
    }
  }, [searchResult, searchResultInfo])

  const topResultCount = useMemo(() => {
    const resultCount = searchResultInfo.length || 0
    if (!searchResult || resultCount === 0) {
      return 0
    }

    const pageSize = searchResult.pageSize || 100
    const totalPages = searchResult.totalPages || 0
    const totalResults = Number.parseInt(searchResult.totalResults || '', 10)

    if (totalPages > 1) {
      const pagedTotal = totalPages * pageSize
      const totalCount = totalResults > 0
        ? Math.min(totalResults, pagedTotal)
        : pagedTotal

      // take min of total count and max results since top 10k results is max
      return Math.min(totalCount, MAX_RESULTS)
    }
    return resultCount
  }, [searchResult, searchResultInfo])

  return (
    <ListPageLayout>
      <SubHeader>
        <>
          <InboxOutlined />{' '}
          {searchResultInfo.length > 0
            ? t('searchPage.totalResult', {
              count: topResultCount,
            })
            : t('searchPage.noResults')}
        </>
        <Spacer />
        <CreateSearchReviewModal
          disabled={searchLoading || formattedData.length === 0}
          query={query || ''}
          searchMethod={searchMethod}
          sourceId={sourceId || ''}
          period={period || ''}
          total={formattedData.length}
          lastModifiedDateStart={startDate || ''}
          lastModifiedDateEnd={endDate || ''}
        />
        <PageContentDropdown
          onExport={handleSearchQueryExport}
          disabled={
            searchLoading || formattedData.length === 0 || !searchResult?.csv
          }
        />
      </SubHeader>
      <Spin spinning={searchLoading}>
        {firstSearchDone ? (
          <SearchTable
            formattedData={formattedData}
            onClick={(recordId) => handleOnRowClick(recordId)}
            maxHeight="calc(100vh - 230px)"
            usePagination
            refreshData={refreshData}
            totalResult={totalResult.total}
            currentPage={currentPage}
            dataSourceVisibility={dataSource?.visibility}
          />
        ) : (
          <FullPageContainer>
            <InitialMessageContainer>
              {t('searchPage.initialMessage')}
            </InitialMessageContainer>
          </FullPageContainer>
        )}
      </Spin>
    </ListPageLayout>
  )
}

export default SearchPage
